import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Helmet } from '@dr.pogodin/react-helmet';
import { Outlet, useLocation } from 'react-router-dom';
import { Header } from './common/Header';
import { Footer } from './common/Footer';
import { CanonicalURL } from './SEO/CanonicalURL';
import { useScrollToTop } from '../hooks/useScrollToTop';
import { captureAttributionData } from '../utils/attribution';
// Font preload URL (Vite hashes the filename, ?url gives us the runtime path)
import { poppins400Woff2 } from '../styles/fonts';

const LayoutWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const Main = styled.main`
  flex: 1;
`;

export const Layout: React.FC = () => {
  useScrollToTop();
  const location = useLocation();
  const hasTrackedInitial = useRef(false);

  // Capture attribution data (GCLID, UTMs) on every route change
  // This ensures GCLID is saved to cookies immediately on landing,
  // not just when forms mount. Critical for multi-page journeys.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    captureAttributionData();
  }, [location]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Skip initial page view (already tracked in index.html)
    if (!hasTrackedInitial.current) {
      hasTrackedInitial.current = true;
      return;
    }

    const path = `${location.pathname}${location.search}${location.hash}`;
    const fullUrl = `${window.location.origin}${path}`;

    // 1. Matomo tracking
    // Note: _paq starts as an array but becomes a tracker object after matomo.js loads
    // We check for .push() method existence, NOT Array.isArray() which would fail after load
    const matomoQueue = (window as any)._paq;
    if (matomoQueue && typeof matomoQueue.push === 'function') {
      matomoQueue.push(['setCustomUrl', fullUrl]);
      matomoQueue.push(['setDocumentTitle', document.title]);
      matomoQueue.push(['trackPageView']);
    }

    // 2. GTM/GA4 dataLayer tracking
    const dataLayer = (window as any).dataLayer;
    if (dataLayer && Array.isArray(dataLayer)) {
      dataLayer.push({
        event: 'virtualPageview',
        page_path: path,
        page_location: fullUrl,
        page_title: document.title,
      });
    }

    // 3. Facebook Pixel tracking
    const fbq = (window as any).fbq;
    if (typeof fbq === 'function') {
      fbq('track', 'PageView');
    }

    // 4. AdRoll tracking
    const adroll = (window as any).__adroll;
    if (adroll && typeof adroll.record_user === 'function') {
      adroll.record_user();
    }
  }, [location]);

  return (
    <LayoutWrapper>
      <Helmet>
        {/* Preload critical font for LCP improvement */}
        <link
          rel="preload"
          as="font"
          type="font/woff2"
          href={poppins400Woff2}
          crossOrigin="anonymous"
        />
      </Helmet>
      <CanonicalURL />
      <Header />
      <Main>
        <Outlet />
      </Main>
      <Footer />
    </LayoutWrapper>
  );
};
